// ============================================================================
// The signed-in business's quote prefix, read synchronously.
// ----------------------------------------------------------------------------
// Render-path formatters (formatQmQuoteNoDisplay, the title generators) cannot
// await, so they read this. Two sources, in order:
//   1. The cached tenant profile (services/tenant-resolver.js): its stored
//      prefix, or its business name's initials (utils/quote-number-prefix.ts).
//   2. The prefix GET /api/auth/me returned at sign-in, which the backend
//      works out with the same rule. The boot check
//      (hooks/use-tenant-session-verification.ts) stores it here before any
//      application surface mounts, so the window before the profile has
//      loaded no longer borrows RJ Bird's "RBB".
// With neither, the answer is '': the Quote # prints bare and a new title
// carries the unresolved stand-in, never another business's letters.
// ============================================================================

import { getInstanceOwner, getInstanceOwnerCached } from './tenant-resolver';
import { effectiveQuoteNumberPrefix, QUOTE_PREFIX_RE } from '../utils/quote-number-prefix';

let sessionPrefix = '';

/** Records the prefix the server named for this session ('' clears it). */
export function setSessionQuoteNumberPrefix(value: unknown): void {
    const prefix = typeof value === 'string' ? value.trim() : '';
    sessionPrefix = QUOTE_PREFIX_RE.test(prefix) ? prefix : '';
}

export function getSessionQuoteNumberPrefix(): string {
    return sessionPrefix;
}

function cachedProfilePrefix(): string {
    try {
        return effectiveQuoteNumberPrefix(getInstanceOwnerCached() as Record<string, unknown> | null);
    } catch {
        // A test double without the cache reader, or a resolver that failed.
        return '';
    }
}

/** This business's prefix, or '' while it is not known yet. Case as stored. */
export function activeQuoteNumberPrefix(): string {
    return cachedProfilePrefix() || sessionPrefix;
}

/** The same answer, after waiting for the profile when it is not cached yet. */
export async function loadActiveQuoteNumberPrefix(): Promise<string> {
    const known = activeQuoteNumberPrefix();
    if (known) return known;
    try {
        const profile = await getInstanceOwner();
        return effectiveQuoteNumberPrefix(profile as Record<string, unknown> | null) || activeQuoteNumberPrefix();
    } catch {
        return activeQuoteNumberPrefix();
    }
}
